import React from 'react';
import PageTitle from '../components/PageTitle';
import { 
  Code2, 
  Database, 
  Smartphone, 
  Shield, 
  Zap,
  GitBranch,
  Package,
  Server,
  Palette,
  CheckCircle,
  ExternalLink,
  Layers,
  Globe,
  Lock,
  Gauge
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Documentacao = () => {
  return (
    <>
      <PageTitle 
        title="Documentação Técnica" 
        description="Arquitetura e especificações técnicas da plataforma"
      />
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="igreja-hero text-white py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Documentação Técnica
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Portfólio
              <span className="block text-church-green">Técnico</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Arquitetura moderna, stack completa e boas práticas de desenvolvimento 
              para uma plataforma robusta e escalável.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/30">React 19</Badge>
              <Badge className="bg-purple-500/20 text-purple-200 border-purple-400/30">Node.js</Badge>
              <Badge className="bg-green-500/20 text-green-200 border-green-400/30">shadcn/ui</Badge>
              <Badge className="bg-cyan-500/20 text-cyan-200 border-cyan-400/30">Tailwind CSS</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section id="arquitetura" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Arquitetura do Sistema
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Estrutura moderna e escalável seguindo as melhores práticas da indústria
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Globe className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-church-blue">Frontend (SPA)</CardTitle>
                  <CardDescription>Cliente React com estado global</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      React 19.1.0 com Hooks
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      React Router para navegação
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Context API para estado
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Server className="h-6 w-6 text-green-600" />
                  </div>
                  <CardTitle className="text-church-blue">Backend (API)</CardTitle>
                  <CardDescription>Servidor Node.js RESTful</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Node.js + Express.js
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      API RESTful estruturada
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Middleware de autenticação
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Database className="h-6 w-6 text-purple-600" />
                  </div>
                  <CardTitle className="text-church-blue">Banco de Dados</CardTitle>
                  <CardDescription>Armazenamento estruturado</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Estrutura relacional
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Relacionamentos otimizados
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      Backup automático
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Architecture Diagram */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-church-blue" />
                  Fluxo de Dados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row items-center justify-between gap-8 p-6 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-2">
                      <Smartphone className="h-8 w-8 text-blue-600" />
                    </div>
                    <p className="font-medium">Cliente React</p>
                    <p className="text-sm text-muted-foreground">Interface do Usuário</p>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-green-500"></div>
                    <div className="w-2 h-2 bg-green-500 rounded-full mx-2"></div>
                    <div className="w-8 h-0.5 bg-gradient-to-r from-green-500 to-purple-500"></div>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-2">
                      <Server className="h-8 w-8 text-green-600" />
                    </div>
                    <p className="font-medium">API Node.js</p>
                    <p className="text-sm text-muted-foreground">Lógica de Negócio</p>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-green-500 to-purple-500"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full mx-2"></div>
                    <div className="w-8 h-0.5 bg-purple-500"></div>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mb-2">
                      <Database className="h-8 w-8 text-purple-600" />
                    </div>
                    <p className="font-medium">Banco de Dados</p>
                    <p className="text-sm text-muted-foreground">Persistência</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section id="stack" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Stack Tecnológica
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Tecnologias modernas e consolidadas para máxima performance e manutenibilidade
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Frontend Stack */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code2 className="h-5 w-5 text-church-blue" />
                    Frontend
                  </CardTitle>
                  <CardDescription>Interface moderna e responsiva</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">React 19.1.0</p>
                      <p className="text-sm text-muted-foreground">Framework JavaScript</p>
                    </div>
                    <Badge variant="secondary">Latest</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">shadcn/ui</p>
                      <p className="text-sm text-muted-foreground">Sistema de componentes</p>
                    </div>
                    <Badge variant="secondary">Modern</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Tailwind CSS</p>
                      <p className="text-sm text-muted-foreground">Framework CSS utility-first</p>
                    </div>
                    <Badge variant="secondary">Optimized</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">React Router</p>
                      <p className="text-sm text-muted-foreground">Navegação SPA</p>
                    </div>
                    <Badge variant="secondary">v6</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Backend Stack */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-church-blue" />
                    Backend
                  </CardTitle>
                  <CardDescription>API robusta e escalável</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Node.js</p>
                      <p className="text-sm text-muted-foreground">Runtime JavaScript</p>
                    </div>
                    <Badge variant="secondary">LTS</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">Express.js</p>
                      <p className="text-sm text-muted-foreground">Framework web</p>
                    </div>
                    <Badge variant="secondary">Fast</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">JWT</p>
                      <p className="text-sm text-muted-foreground">Autenticação segura</p>
                    </div>
                    <Badge variant="secondary">Secure</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">CORS & Helmet</p>
                      <p className="text-sm text-muted-foreground">Middleware de segurança</p>
                    </div>
                    <Badge variant="secondary">Protected</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Development Tools */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-church-blue" />
                  Ferramentas de Desenvolvimento
                </CardTitle>
                <CardDescription>Ambiente de desenvolvimento moderno e produtivo</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-church-green" />
                      Versionamento
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Git Flow</li>
                      <li>• Commits semânticos</li>
                      <li>• Branching strategy</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Gauge className="h-4 w-4 text-church-green" />
                      Performance
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Bundle optimization</li>
                      <li>• Code splitting</li>
                      <li>• Lazy loading</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Lock className="h-4 w-4 text-church-green" />
                      Segurança
                    </h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Sanitização de dados</li>
                      <li>• Validação de entrada</li>
                      <li>• Rate limiting</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features & Best Practices */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-church-blue mb-6">
                Funcionalidades Implementadas
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Recursos avançados desenvolvidos seguindo as melhores práticas da indústria
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Shield className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">Autenticação JWT</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Sistema completo de login/registro com tokens seguros e refresh automático.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Code2 className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">Editor Markdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Editor avançado com preview em tempo real e suporte completo ao GitHub Flavored Markdown.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Smartphone className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">Design Responsivo</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Interface adaptativa otimizada para desktop, tablet e smartphone com navigation móvel.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Palette className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">Sistema de Temas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Alternância entre modo claro e escuro com persistência local e transições suaves.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Zap className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Otimizações avançadas com lazy loading, memoização e bundle splitting.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <Database className="h-8 w-8 text-church-blue mb-2" />
                  <CardTitle className="text-lg">CRUD Completo</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">
                    Sistema completo de gerenciamento de posts com upload de imagens e controle de permissões.
                  </p>
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Implementado
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-church-blue">Especificações Técnicas</CardTitle>
                <CardDescription>Detalhamento técnico completo da implementação</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-church-blue" />
                      Dependências Frontend
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>React</span>
                        <Badge variant="outline">19.1.0</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>React Router DOM</span>
                        <Badge variant="outline">6.x</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Tailwind CSS</span>
                        <Badge variant="outline">3.4.x</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Radix UI</span>
                        <Badge variant="outline">Latest</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Lucide React</span>
                        <Badge variant="outline">Latest</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Server className="h-4 w-4 text-church-blue" />
                      Dependências Backend
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Node.js</span>
                        <Badge variant="outline">18+ LTS</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Express.js</span>
                        <Badge variant="outline">4.x</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>JSON Web Token</span>
                        <Badge variant="outline">9.x</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>bcryptjs</span>
                        <Badge variant="outline">2.x</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>cors</span>
                        <Badge variant="outline">2.x</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-border">
                  <h4 className="font-semibold mb-3">Características do Projeto</h4>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>TypeScript Ready</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>PWA Compatible</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>SEO Optimized</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Accessibility (WCAG)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Performance Optimized</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Mobile First</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Documentacao;